import { moduleTypeLabel } from '../../../../utils/moduleTypeLabel';
import React, { useState, useCallback, useMemo } from 'react';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import toast from 'react-hot-toast';
import {
  useSections,
  fetchModules,
  useModule,
  createSection,
  updateSection as updateSectionApi,
  deleteSection as deleteSectionApi,
  createModule,
  updateModule as updateModuleApi,
  deleteModule as deleteModuleApi,
  reorderModules as reorderModulesApi,
} from '../../../../services/courses';
import { uploadFile, useImageUrl } from '../../../../services/uploads';
import type { ModuleType } from '../../../../types/course';
import { PENDING_PLACEHOLDER } from '../../../../components/courses/editors/contentPlaceholder';

function uid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

function defaultDetailsForType(type: ModuleType): Record<string, unknown> {
  if (type === 'evaluation') {
    // Backend validates at creation: items[] non-empty, question 1-2000 chars,
    // options each with text, correct_answers ⊆ option ids with length ≥ 1.
    const opt1 = uid('opt');
    const opt2 = uid('opt');
    return {
      items: [
        {
          id: uid('item'),
          type: 'multiple_choice',
          order_index: 0,
          points: 1,
          config: {
            question: 'New question',
            options: [
              { id: opt1, text: 'Option 1' },
              { id: opt2, text: 'Option 2' },
            ],
            correct_answers: [opt1],
          },
        },
      ],
      passing_grade_percent: null,
      max_attempts: null,
      shuffle_items: false,
    };
  }
  if (type === 'feedback') {
    // Backend requires items[] non-empty, question 1-2000 chars.
    return {
      items: [
        {
          id: uid('item'),
          type: 'likert_5_scale',
          order_index: 0,
          required: true,
          config: { question: 'New question' },
        },
      ],
      anonymous: false,
    };
  }

  // Content modules. Backend requires specific keys on the `details` object:
  //   rich_text          → content (any string, empty allowed)
  //   image_embed        → image_url (string, 1-2000 chars)
  //   video_url_embed    → video_url (1-2000)
  //   pdf_file           → file_url  (1-2000)
  //   source_code_file   → file_url  (1-2000)
  //   scorm_zip          → file_url  (1-2000)
  //   video_meeting_link → meeting_url (1-2000)
  // For every URL-style field we seed a recognisable placeholder; the editor
  // treats that value as "empty" so the DropZone / URL input is shown.
  switch (type) {
    case 'rich_text':
      return { content: '' };
    case 'image_embed':
      return { image_url: PENDING_PLACEHOLDER };
    case 'video_url_embed':
      return { video_url: PENDING_PLACEHOLDER };
    case 'pdf_file':
      return { file_url: PENDING_PLACEHOLDER };
    case 'source_code_file':
      return { file_url: PENDING_PLACEHOLDER };
    case 'scorm_zip':
      return { file_url: PENDING_PLACEHOLDER };
    case 'video_meeting_link':
      return { meeting_url: PENDING_PLACEHOLDER };
    default:
      return {};
  }
}
import StepCard from '../shared/StepCard';
import CoursePreviewOverlay from '../shared/CoursePreviewOverlay';
import AddSectionModal from '../modals/AddSectionModal';
import ContentTypePickerModal from '../modals/ContentTypePickerModal';
import ContentEditorOverlay from '../modals/ContentEditorOverlay';
import type { ContentEditorOverlayModule } from '../modals/ContentEditorOverlay';
import { REMINDER_STORAGE_KEYS } from '../wizardTypes';
import type { Step2ModalState, ContentTypeOption } from '../wizardTypes';
import ReminderBanner from '../ReminderBanner';
import { useDismissible } from '@/hooks/learner/useMemberConfig';

// ─── Local types ──────────────────────────────────────────────────────────────

interface ModuleItem {
  id: string;
  title: string;
  type: ModuleType;
  order_index: number;
  details: Record<string, unknown>;
}

interface Section {
  id: string;
  title: string;
  image_url: string | null;
  order_index: number;
  headerUploaded: boolean;
  modules: ModuleItem[];
}

export interface CourseContentStepProps {
  courseId: string;
  disabled?: boolean;
  onSaved: () => void;
  /** Stagger delay forwarded to StepCard (seconds). */
  entranceDelay?: number;
  /** True when the wizard is editing an existing course; the step starts collapsed. */
  editMode?: boolean;
}

// ─── Helper: human-readable section number label ──────────────────────────────

function sectionLabel(idx: number, section: Section): string {
  return `Section ${idx + 1}: ${section.title}${section.headerUploaded ? ', Header Uploaded' : ''}`;
}

// ─── Trash icon ───────────────────────────────────────────────────────────────

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CourseContentStep({
  courseId,
  disabled,
  onSaved,
  entranceDelay,
  editMode = false,
}: CourseContentStepProps) {
  const queryClient = useQueryClient();
  // Mirror the Step 1/4 pattern so StepCard auto-collapses on Save. When the
  // wizard opens in edit mode the step starts collapsed since content already
  // exists; the user clicks Edit on the StepCard to make changes.
  const [saved, setSaved] = useState(editMode);
  const handleSaved = () => {
    onSaved();
    setSaved(true);
  };
  const { data: rawSections, isLoading: sectionsLoading } = useSections(courseId);

  // Fetch modules for each section in parallel
  const sectionIds = useMemo(
    () => (rawSections ?? []).map((s) => s.id),
    [rawSections]
  );

  const modulesQueries = useQueries({
    queries: sectionIds.map((sectionId) => ({
      queryKey: ['courses', courseId, 'sections', sectionId, 'modules'],
      queryFn: () => fetchModules(courseId, sectionId),
      enabled: !!courseId && sectionIds.length > 0,
      staleTime: 60 * 1000,
    })),
  });

  const modulesLoading = modulesQueries.some((q) => q.isLoading && !q.data);
  const loading = sectionsLoading || modulesLoading;

  // Stable key for modules data — only recomputes when actual data changes
  const modulesDataKey = modulesQueries.map((q) => q.dataUpdatedAt).join(',');

  // Build a map of sectionId → modules from the parallel queries
  const modulesBySectionId = useMemo(() => {
    const map = new Map<string, ModuleItem[]>();
    sectionIds.forEach((sectionId, i) => {
      const data = modulesQueries[i]?.data;
      if (data) {
        map.set(
          sectionId,
          data.map((m) => ({
            id: m.id,
            title: m.title,
            type: m.type,
            order_index: m.order_index,
            details: (m.details ?? {}) as Record<string, unknown>,
          }))
        );
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds, modulesDataKey]);

  // Derive sections with their modules
  const sections: Section[] = (rawSections ?? []).map((s) => ({
    id: s.id,
    title: s.title,
    image_url: s.image_url ?? null,
    order_index: s.order_index,
    headerUploaded: false,
    modules: modulesBySectionId.get(s.id) ?? [],
  }));

  const [modalState, setModalState] = useState<Step2ModalState>({ kind: 'none' });
  // Resolve the editing section's stored image key to a signed URL for the
  // modal preview (image_url on a section is an S3 key, not a display URL).
  const [editSectionImageUrl] = useImageUrl(
    modalState.kind === 'editSection' ? modalState.imageUrl : null
  );
  const [dragState, setDragState] = useState<{
    sectionId: string;
    moduleId: string;
    overIdx: number | null;
  } | null>(null);
  // The module currently open in the full-page editor overlay (lightweight pointer)
  const [editingModule, setEditingModule] = useState<{
    sectionId: string;
    moduleId: string;
  } | null>(null);

  // Fetch the actual module data from the API when an editor is open
  const { data: liveModule } = useModule(
    editingModule ? courseId : undefined,
    editingModule?.sectionId,
    editingModule?.moduleId
  );

  // Build the overlay module from the live query data
  const overlayModule: ContentEditorOverlayModule | null = liveModule
    ? {
        id: liveModule.id,
        title: liveModule.title,
        type: liveModule.type,
        image_url: liveModule.image_url ?? null,
        details: (liveModule.details ?? {}) as Record<string, unknown>,
      }
    : null;

  const [creatingSec, setCreatingSec] = useState(false);
  // Reminder dismissal is backed by the member's `/me/config`; the banner hides
  // itself when dismissed, and we hide its wrapper too while config loads.
  const { dismissed: reminderDismissed, isLoading: reminderLoading } = useDismissible(
    REMINDER_STORAGE_KEYS[2]
  );
  const reminderVisible = !reminderLoading && !reminderDismissed;
  const [creatingMod, setCreatingMod] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const hasModules = sections.some((s) => s.modules.length > 0);

  // Invalidate sections + all child module queries
  const invalidateSections = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['courses', courseId, 'sections'] });
  }, [queryClient, courseId]);

  // Invalidate modules for a specific section
  const invalidateModules = useCallback((sectionId: string) => {
    queryClient.invalidateQueries({ queryKey: ['courses', courseId, 'sections', sectionId, 'modules'] });
  }, [queryClient, courseId]);

  // ─── Section CRUD ────────────────────────────────────────────────────────

  const handleCreateSection = async (
    title: string,
    imageFile: File | null
  ) => {
    if (creatingSec) return;
    setCreatingSec(true);
    try {
      const section = await createSection(courseId, { title });

      if (imageFile) {
        try {
          const uploadResult = await uploadFile({
            file: imageFile,
            resource_type: 'section_cover',
            resource_id: section.id,
          });
          // Store the S3 key on the section
          await updateSectionApi(courseId, section.id, { image_url: uploadResult.key });
        } catch {
          toast.error('Section created but image upload failed');
        }
      }

      invalidateSections();
      setModalState({ kind: 'none' });
      toast.success('Section created');
    } catch {
      toast.error('Failed to create section');
    } finally {
      setCreatingSec(false);
    }
  };

  const handleUpdateSection = async (
    sectionId: string,
    title: string,
    imageFile: File | null,
    imageRemoved: boolean
  ) => {
    if (creatingSec) return;
    setCreatingSec(true);
    try {
      await updateSectionApi(courseId, sectionId, { title });

      if (imageFile) {
        try {
          const uploadResult = await uploadFile({
            file: imageFile,
            resource_type: 'section_cover',
            resource_id: sectionId,
          });
          await updateSectionApi(courseId, sectionId, { image_url: uploadResult.key });
        } catch {
          toast.error('Section saved but image upload failed');
        }
      } else if (imageRemoved) {
        await updateSectionApi(courseId, sectionId, { image_url: null });
      }

      invalidateSections();
      setModalState({ kind: 'none' });
      toast.success('Section updated');
    } catch {
      toast.error('Failed to update section');
    } finally {
      setCreatingSec(false);
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!window.confirm('Delete this section and all its content?')) return;
    try {
      await deleteSectionApi(courseId, sectionId);
      invalidateSections();
      if (editingModule?.sectionId === sectionId) setEditingModule(null);
      toast('Section deleted', { icon: 'ℹ️' });
    } catch {
      toast.error('Failed to delete section');
    }
  };

  // ─── Module CRUD ─────────────────────────────────────────────────────────

  const handleCreateModule = async (
    sectionId: string,
    title: string,
    type: ModuleType,
    imageFile: File | null
  ) => {
    if (creatingMod) return;
    setCreatingMod(true);
    try {
      const mod = await createModule(courseId, sectionId, { title, type, details: defaultDetailsForType(type) });

      if (imageFile) {
        try {
          const uploadResult = await uploadFile({
            file: imageFile,
            resource_type: 'module_cover',
            resource_id: mod.id,
          });
          // Store the S3 key on the module
          await updateModuleApi(courseId, sectionId, mod.id, { image_url: uploadResult.key });
        } catch {
          toast.error('Content created but image upload failed');
        }
      }

      invalidateModules(sectionId);

      setModalState({ kind: 'none' });
      // Open the editor overlay immediately
      setEditingModule({ sectionId, moduleId: mod.id });
      toast.success('Content created');
    } catch {
      toast.error('Failed to create content');
    } finally {
      setCreatingMod(false);
    }
  };

  const handleUpdateModule = async (
    sectionId: string,
    moduleId: string,
    updates: Record<string, unknown>
  ) => {
    try {
      const updated = await updateModuleApi(courseId, sectionId, moduleId, updates);
      // Do NOT invalidate queries here — invalidating on every auto-save
      // (every 1.5s) causes a cascade of refetches. Instead, write the PUT's
      // own response straight into the per-module cache: it is the
      // authoritative post-save state, so a close-triggered refetch racing the
      // save can never resurrect stale data on the next open.
      queryClient.setQueryData(
        ['courses', courseId, 'sections', sectionId, 'modules', moduleId],
        updated
      );
    } catch {
      toast.error('Failed to save content');
    }
  };

  const handleReorderModules = async (
    sectionId: string,
    fromIdx: number,
    toIdx: number
  ) => {
    if (fromIdx === toIdx) return;
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;
    const mods = [...section.modules];
    const [moved] = mods.splice(fromIdx, 1);
    mods.splice(toIdx, 0, moved);
    const items = mods.map((m, i) => ({ id: m.id, order_index: i }));

    // Optimistic update: reorder the cached module list so the layout animation
    // fires immediately on drop instead of waiting for the server round-trip.
    const queryKey = ['courses', courseId, 'sections', sectionId, 'modules'];
    const previous = queryClient.getQueryData<ModuleItem[]>(queryKey);
    queryClient.setQueryData<ModuleItem[]>(queryKey, (old) => {
      if (!old) return old;
      const next = [...old];
      const [m] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, m);
      return next.map((entry, i) => ({ ...entry, order_index: i }));
    });

    try {
      await reorderModulesApi(courseId, sectionId, { items });
      invalidateModules(sectionId);
    } catch {
      // Rollback on failure
      if (previous) queryClient.setQueryData(queryKey, previous);
      toast.error('Failed to reorder content');
    }
  };

  const handleDeleteModule = async (sectionId: string, moduleId: string) => {
    if (!window.confirm('Delete this content?')) return;
    try {
      await deleteModuleApi(courseId, sectionId, moduleId);
      invalidateModules(sectionId);
      if (editingModule?.moduleId === moduleId) setEditingModule(null);
      toast('Content deleted', { icon: 'ℹ️' });
    } catch {
      toast.error('Failed to delete content');
    }
  };

  // ─── Modal flow ──────────────────────────────────────────────────────────

  const handleContentTypeSave = async (option: ContentTypeOption) => {
    if (modalState.kind !== 'pickContentType') return;
    const { sectionId } = modalState;
    await handleCreateModule(sectionId, option.label, option.moduleType, null);
  };

  // ─── Overlay save (proxies through handleUpdateModule) ───────────────────

  const handleOverlaySave = async (moduleId: string, updates: Record<string, unknown>) => {
    if (!editingModule) return;
    await handleUpdateModule(editingModule.sectionId, moduleId, updates);
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <StepCard
        stepNumber={2}
        title="Content"
        disabled={disabled}
        onSave={handleSaved}
        saved={saved}
        onPreview={hasModules ? () => setPreviewOpen(true) : undefined}
        entranceDelay={entranceDelay}
      >
        <div
          className={reminderVisible ? 'grid grid-cols-1 lg:grid-cols-[1fr_376px] gap-6 mt-5 items-start' : 'flex flex-col gap-6 mt-5'}
        >
          {/* ── Left: section list ── */}
          <div>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      height: '80px',
                      borderRadius: '12px',
                      background: 'rgba(189,188,201,0.15)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <AnimatePresence>
                  {sections.map((section, idx) => (
                    <motion.div
                      key={section.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, ease: EASE_OUT }}
                      className="rounded-2xl border-2 border-dashed border-primary-500 overflow-visible px-4 sm:px-8 py-4 flex flex-col items-center gap-4"
                    >
                      {/* ── Section header + content items tree ── */}
                      <div className="w-full max-w-[420px] relative">
                        {/* Section title pill */}
                        <div className="flex items-center justify-between border-2 border-primary-500 rounded-full h-10 pl-5 pr-3">
                          <p className="text-sm font-bold text-text-primary truncate leading-tight">
                            {sectionLabel(idx, section)}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleDeleteSection(section.id)}
                            title="Delete section"
                            className="p-1 rounded text-text-disabled hover:text-error-500 active:scale-[0.94] transition-[color,transform] duration-150 shrink-0"
                          >
                            <TrashIcon />
                          </button>
                        </div>

                        {/* Content items — connected by a tree line */}
                        <AnimatePresence initial={false}>
                        {section.modules.map((mod, modIdx) => {
                          const isDragging =
                            dragState?.sectionId === section.id &&
                            dragState?.moduleId === mod.id;
                          const isDropTarget =
                            dragState?.sectionId === section.id &&
                            dragState?.overIdx === modIdx &&
                            dragState?.moduleId !== mod.id;
                          return (
                            <motion.div
                              key={mod.id}
                              layout
                              initial={{ opacity: 0, y: -6, scale: 0.98 }}
                              animate={{
                                opacity: isDragging ? 0.35 : 1,
                                y: 0,
                                scale: isDragging ? 0.98 : 1,
                              }}
                              exit={{ opacity: 0, y: -4, scale: 0.96, height: 0 }}
                              transition={{
                                layout: { type: 'spring', stiffness: 520, damping: 42, mass: 0.6 },
                                opacity: { duration: 0.18, ease: EASE_OUT },
                                scale: { duration: 0.18, ease: EASE_OUT },
                                y: { duration: 0.18, ease: EASE_OUT },
                                height: { duration: 0.2, ease: EASE_OUT },
                              }}
                              onDragOver={(e) => {
                                if (!dragState || dragState.sectionId !== section.id) return;
                                e.preventDefault();
                                if (dragState.overIdx !== modIdx) {
                                  setDragState({ ...dragState, overIdx: modIdx });
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (!dragState || dragState.sectionId !== section.id) return;
                                const fromIdx = section.modules.findIndex(
                                  (m) => m.id === dragState.moduleId
                                );
                                if (fromIdx >= 0) handleReorderModules(section.id, fromIdx, modIdx);
                                setDragState(null);
                              }}
                              className="relative pl-10 group/mod"
                              style={{
                                marginTop: modIdx === 0 ? 0 : '4px',
                                borderTop: isDropTarget
                                  ? '2px solid rgb(132,90,212)'
                                  : '2px solid transparent',
                                willChange: 'transform',
                              }}
                            >
                              {/* Tree connector line */}
                              <svg
                                width="22"
                                className="absolute left-4 overflow-visible"
                                style={{
                                  top: modIdx === 0 ? '-8px' : '-12px',
                                  height: modIdx === 0 ? 'calc(50% + 8px)' : 'calc(50% + 12px)',
                                }}
                                aria-hidden="true"
                              >
                                <path
                                  d={`M0 0 V${modIdx === 0 ? 32 : 36} H20`}
                                  fill="none"
                                  className="stroke-grey-500/50"
                                  strokeWidth="1.5"
                                  strokeDasharray="4 3"
                                />
                              </svg>

                              {/* Row: drag handle + pill + delete */}
                              <div className="flex items-center gap-1">
                                {/* Drag handle */}
                                <span
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = 'move';
                                    setDragState({
                                      sectionId: section.id,
                                      moduleId: mod.id,
                                      overIdx: null,
                                    });
                                  }}
                                  onDragEnd={() => setDragState(null)}
                                  title="Drag to reorder"
                                  className="shrink-0 w-5 h-5 flex items-center justify-center text-text-disabled cursor-grab active:cursor-grabbing hover:text-text-secondary transition-colors"
                                  aria-label="Reorder content"
                                >
                                  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" aria-hidden="true">
                                    <circle cx="3" cy="2" r="1" fill="currentColor" />
                                    <circle cx="9" cy="2" r="1" fill="currentColor" />
                                    <circle cx="3" cy="7" r="1" fill="currentColor" />
                                    <circle cx="9" cy="7" r="1" fill="currentColor" />
                                    <circle cx="3" cy="12" r="1" fill="currentColor" />
                                    <circle cx="9" cy="12" r="1" fill="currentColor" />
                                  </svg>
                                </span>

                                {/* Content item pill */}
                                <button
                                  type="button"
                                  onClick={() => setEditingModule({ sectionId: section.id, moduleId: mod.id })}
                                  className={`flex items-center gap-2 flex-1 min-w-0 rounded-full h-9 px-4 cursor-pointer active:scale-[0.99] transition-[border-color,background-color,transform] duration-150 ${
                                    editingModule?.moduleId === mod.id
                                      ? 'bg-primary-500/[0.08] border-2 border-primary-500'
                                      : 'bg-grey-500/[0.08] border border-grey-500/40 hover:border-primary-500 hover:bg-primary-500/[0.04]'
                                  }`}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0 text-text-secondary">
                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="currentColor" opacity="0.15" />
                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                  <span className="text-xs font-semibold text-text-secondary truncate">
                                    &quot;{mod.title}&quot; {moduleTypeLabel(mod.type).toLowerCase()}
                                  </span>
                                </button>

                                {/* Floating delete button */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteModule(section.id, mod.id);
                                  }}
                                  title="Delete content"
                                  aria-label="Delete content"
                                  className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-text-disabled bg-surface-elevated border border-grey-500/40 hover:text-error-500 hover:border-error-500 active:scale-[0.92] opacity-0 group-hover/mod:opacity-100 transition-[opacity,color,border-color,transform] duration-150"
                                >
                                  <TrashIcon />
                                </button>
                              </div>
                            </motion.div>
                          );
                        })}
                        </AnimatePresence>
                      </div>

                      {/* ── Bottom buttons: Add Content + | Update ── */}
                      <div className="flex gap-3 items-center">
                        <button
                          type="button"
                          onClick={() => setModalState({ kind: 'pickContentType', sectionId: section.id })}
                          className="border border-text-secondary text-text-secondary rounded-full px-4 py-1.5 text-xs font-bold cursor-pointer hover:border-primary-500 hover:text-primary-500 active:scale-[0.97] transition-[border-color,color,transform] duration-150"
                        >
                          Add Content +
                        </button>
                        {/* Update */}
                        <button
                          type="button"
                          onClick={() =>
                            setModalState({
                              kind: 'editSection',
                              sectionId: section.id,
                              title: section.title,
                              imageUrl: section.image_url,
                            })
                          }
                          className="flex-1 min-w-[100px] max-w-[180px] rounded-full px-4 py-1.5 bg-grey-900 text-white dark:bg-grey-100 dark:text-grey-900 dark:hover:bg-grey-200 text-xs font-bold cursor-pointer hover:bg-grey-800 active:scale-[0.97] transition-[background-color,transform] duration-150 text-center"
                        >
                          Update
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* ── "+ Add Section" — Figma spec ── */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setModalState({ kind: 'addSection' })}
                    className="inline-flex items-center justify-center gap-2.5 w-[325px] max-w-full h-[41px] px-[21px] py-[15px] rounded-full border-[0.7px] border-primary-500/[0.48] font-sans font-bold text-[15px] leading-6 text-text-primary transition-[transform,filter] duration-150 hover:scale-[1.01] hover:brightness-105 active:scale-[0.99]"
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(184,156,229,0.114) 14.9%, rgb(var(--color-primary) / 0.15) 100%)',
                    }}
                  >
                    + Add Section
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Right: reminder card ── */}
          {reminderVisible && (
            <div style={{ alignSelf: 'start' }}>
              <ReminderBanner
                storageKey={REMINDER_STORAGE_KEYS[2]}
                title="Reminder"
                message={`Please, drag and drop the content within the sections in the same order as you want the learners to access each content. Example: The first content in the first section will be accessed first and so on. Each content will be unblocked as the previous one is finished.\n\nIf you want the learners to navigate freely, mark the checkbox below the sections!`}
              />
            </div>
          )}
        </div>

        {/* Pulse animation for skeleton loaders */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
      </StepCard>

      {/* ── Modals ── */}
      <AddSectionModal
        isOpen={modalState.kind === 'addSection'}
        onClose={() => setModalState({ kind: 'none' })}
        onCreateSection={handleCreateSection}
        creating={creatingSec}
      />

      <AddSectionModal
        isOpen={modalState.kind === 'editSection'}
        mode="edit"
        initialTitle={modalState.kind === 'editSection' ? modalState.title : ''}
        initialImageUrl={editSectionImageUrl}
        onClose={() => setModalState({ kind: 'none' })}
        onCreateSection={(title, imageFile, imageRemoved) => {
          if (modalState.kind === 'editSection') {
            handleUpdateSection(modalState.sectionId, title, imageFile, imageRemoved);
          }
        }}
        creating={creatingSec}
      />

      <ContentTypePickerModal
        isOpen={modalState.kind === 'pickContentType'}
        onClose={() => setModalState({ kind: 'none' })}
        onSelect={(option) => {
          handleContentTypeSave(option);
        }}
      />

      {/* ── Full-page editor overlay ── */}
      <ContentEditorOverlay
        isOpen={editingModule !== null}
        sectionId={editingModule?.sectionId ?? ''}
        courseId={courseId}
        module={overlayModule}
        onClose={() => {
          const sid = editingModule?.sectionId;
          setEditingModule(null);
          // Refresh modules after closing so the list shows updated titles/content
          if (sid) invalidateModules(sid);
        }}
        onSave={handleOverlaySave}
      />

      {/* ── Full-course preview ── */}
      <CoursePreviewOverlay
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        courseId={courseId}
      />
    </>
  );
}
